/** „Vaše volby" — the running summary beside the wizard's steps (KAN-1473). */

import { useTranslation } from "react-i18next";

import { regionName, type WizardCarrier } from "./model";
import type { WizardSelection } from "./useWizardSelection";

/**
 * Do kolika položek se vypisují JMÉNA; nad tím se vypíše počet.
 *
 * KAN-1501, rozhodnutí vlastníka 2026-09-20. Do té doby panel mluvil
 * dvěma jazyky naráz: Stroje a Materiály ukazovaly počty („1×", „2×"),
 * Země a Dopravci jména („CZ · SK", „Zásilkovna, Osobní odběr"). Na
 * jednom panelu, vedle sebe — a „2×" laikovi neřekne, co si vybral.
 *
 * Čtyřka je kompromis, ne magie: panel je úzký sloupec a delší výčet
 * by se v něm rozlil přes několik řádků, takže od pěti výš je počet
 * čitelnější než jména.
 */
const NAMES_UP_TO = 4;

export function ChoicesAside({
  w,
  carriers,
  company,
}: {
  w: WizardSelection;
  carriers: WizardCarrier[];
  /** The seller's name; leave it out where there is no company step. */
  company?: string;
}) {
  const { t, i18n } = useTranslation("admin");
  const {
    startEmpty,
    chosen,
    machineCount,
    materialCount,
    machineNames,
    materialNames,
    countryCodes,
    carrierCodes,
  } = w;
  const none = <span className="none">{t("onboardingStart.aside.none")}</span>;
  const carrierNames = carriers
    .filter((c) => carrierCodes.includes(c.code))
    .map((c) => c.name);

  /**
   * Jména, dokud se jich vejde nejvýš :data:`NAMES_UP_TO`; jinak počet.
   *
   * ``count`` se předává zvlášť, protože u strojů to NENÍ délka
   * seznamu jmen: jeden řádek může být „3× tentýž stroj", takže se
   * vypisuje jedno jméno, ale počítají se tři kusy.
   */
  const row = (names: string[], count = names.length, sep = ", ") => {
    if (count <= 0) return none;
    if (names.length > 0 && names.length <= NAMES_UP_TO) {
      return names.join(sep);
    }
    return t("onboardingStart.aside.count", { count });
  };

  return (
    <aside className="aside">
      <h4>{t("onboardingStart.aside.title")}</h4>
      <dl>
        <div>
          <dt>{t("onboardingStart.aside.operations")}</dt>
          <dd>
            {startEmpty
              ? t("onboardingStart.aside.emptyShop")
              : row(chosen.map((op) => op.display_name))}
          </dd>
        </div>
        <div>
          <dt>{t("onboardingStart.aside.machines")}</dt>
          <dd>{row(machineNames, machineCount)}</dd>
        </div>
        <div>
          <dt>{t("onboardingStart.aside.materials")}</dt>
          <dd>{row(materialNames, materialCount)}</dd>
        </div>
        <div>
          <dt>{t("onboardingStart.aside.countries")}</dt>
          {/* KAN-2019 — jména zemí („Česko"), ne kódy („CZ"): týž název,
              jaký ukazuje krok Doprava. */}
          <dd data-testid="onboarding-aside-countries">
            {row(
              countryCodes.map((code) => regionName(i18n.language, code)),
              countryCodes.length,
            )}
          </dd>
        </div>
        <div>
          <dt>{t("onboardingStart.aside.carriers")}</dt>
          <dd>{row(carrierNames)}</dd>
        </div>
        {company !== undefined ? (
          <div>
            <dt>{t("onboardingStart.aside.company")}</dt>
            <dd>{company.trim() || none}</dd>
          </div>
        ) : null}
      </dl>
    </aside>
  );
}
